import React, { useState, useEffect } from 'react';
import { useColeccionesStore } from '../../stores/useColeccionesStore';

const EMOJI_SUGGESTIONS = [
  '🌷', '🎄', '🔥', '⭐', '💝', '🎁', '🏷️', '✨', '🛍️', '💅', '💄', '👗', '🎃', '🌸', '❄️', '🎉',
];

export interface ColeccionPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  empresaId: string;
  selectedProductIds: string[];
  onSuccess: () => void;
}

export const ColeccionPickerModal: React.FC<ColeccionPickerModalProps> = ({
  isOpen,
  onClose,
  empresaId,
  selectedProductIds,
  onSuccess,
}) => {
  const { colecciones, fetchColecciones, createColeccion, addProductosToColeccion, generateSlug, isLoading } =
    useColeccionesStore();

  const [mode, setMode] = useState<'pick' | 'create'>('pick');
  const [selectedColeccionId, setSelectedColeccionId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);

  const [newNombre, setNewNombre] = useState('');
  const [newDescripcion, setNewDescripcion] = useState('');
  const [newIcono, setNewIcono] = useState('');
  const [newColor, setNewColor] = useState('#1A56DB');
  const [newFechaInicio, setNewFechaInicio] = useState('');
  const [newFechaFin, setNewFechaFin] = useState('');
  const [isTemporary, setIsTemporary] = useState(false);

  useEffect(() => {
    if (isOpen && empresaId) {
      void fetchColecciones(empresaId);
      setMode('pick');
      setSelectedColeccionId(null);
      setSearchTerm('');
      setNewNombre('');
      setNewDescripcion('');
      setNewIcono('');
      setNewColor('#1A56DB');
      setNewFechaInicio('');
      setNewFechaFin('');
      setIsTemporary(false);
    }
  }, [isOpen, empresaId, fetchColecciones]);

  const filtered = colecciones.filter((c) => c.nombre.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleAddToExisting = async () => {
    if (!selectedColeccionId) return;
    setSaving(true);
    try {
      await addProductosToColeccion(selectedColeccionId, selectedProductIds);
      onSuccess();
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAndAdd = async () => {
    if (!newNombre.trim() || !empresaId) return;
    setSaving(true);
    try {
      const created = await createColeccion({
        empresa_id: empresaId,
        nombre: newNombre.trim(),
        slug: generateSlug(newNombre.trim()),
        descripcion: newDescripcion || undefined,
        icono: newIcono || undefined,
        color: newColor,
        activa: true,
        destacada: false,
        orden: colecciones.length,
        fecha_inicio:
          isTemporary && newFechaInicio ? new Date(newFechaInicio + 'T12:00:00').toISOString() : null,
        fecha_fin: isTemporary && newFechaFin ? new Date(newFechaFin + 'T23:59:59').toISOString() : null,
      });
      if (created) {
        await addProductosToColeccion(created.id, selectedProductIds);
      }
      onSuccess();
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Agregar a colección</h2>
          <p className="text-sm text-gray-500 mt-1">
            {selectedProductIds.length} producto{selectedProductIds.length !== 1 ? 's' : ''} seleccionado
            {selectedProductIds.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="px-6 pt-4 flex gap-2">
          <button
            type="button"
            onClick={() => setMode('pick')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              mode === 'pick' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Colección existente
          </button>
          <button
            type="button"
            onClick={() => setMode('create')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              mode === 'create' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            + Crear nueva
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
          {mode === 'pick' ? (
            <>
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar colección..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 mb-3"
              />
              {isLoading && <p className="text-sm text-gray-500">Cargando…</p>}
              {!isLoading && filtered.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">
                    {colecciones.length === 0 ? 'No tienes colecciones aún' : 'No se encontraron coincidencias'}
                  </p>
                  <button
                    type="button"
                    onClick={() => setMode('create')}
                    className="text-indigo-600 text-sm font-medium mt-2 hover:text-indigo-800"
                  >
                    Crear una colección
                  </button>
                </div>
              )}
              <div className="space-y-2">
                {filtered.map((col) => (
                  <button
                    key={col.id}
                    type="button"
                    onClick={() => setSelectedColeccionId(selectedColeccionId === col.id ? null : col.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      selectedColeccionId === col.id
                        ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {col.icono && <span className="text-xl">{col.icono}</span>}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{col.nombre}</p>
                        <p className="text-xs text-gray-500">
                          {col.productos_count ?? 0} producto{(col.productos_count ?? 0) !== 1 ? 's' : ''}
                          {col.fecha_fin && (
                            <span className="ml-2">
                              Hasta{' '}
                              {new Date(col.fecha_fin).toLocaleDateString('es-CO', {
                                day: 'numeric',
                                month: 'short',
                              })}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  value={newNombre}
                  onChange={(e) => setNewNombre(e.target.value)}
                  placeholder="Ej: Día de las Madres, Ofertas…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción (opcional)</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  rows={2}
                  value={newDescripcion}
                  onChange={(e) => setNewDescripcion(e.target.value)}
                />
              </div>
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icono</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-center text-xl"
                    value={newIcono}
                    onChange={(e) => setNewIcono(e.target.value)}
                    maxLength={4}
                  />
                  <div className="flex flex-wrap gap-1 mt-2 max-h-24 overflow-y-auto">
                    {EMOJI_SUGGESTIONS.map((em) => (
                      <button
                        key={em}
                        type="button"
                        onClick={() => setNewIcono(em)}
                        className={`w-8 h-8 rounded text-lg hover:bg-gray-100 ${
                          newIcono === em ? 'bg-indigo-100 ring-2 ring-indigo-300' : ''
                        }`}
                      >
                        {em}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="w-24">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <input
                    type="color"
                    className="w-full h-10 rounded-lg border border-gray-300 cursor-pointer"
                    value={newColor}
                    onChange={(e) => setNewColor(e.target.value)}
                  />
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg p-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isTemporary}
                    onChange={(e) => setIsTemporary(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-indigo-600"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Colección temporal</span>
                    <p className="text-xs text-gray-500">Visible solo entre las fechas indicadas (catálogo público)</p>
                  </div>
                </label>
                {isTemporary && (
                  <div className="flex gap-3 mt-3 ml-7">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">Desde</label>
                      <input
                        type="date"
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                        value={newFechaInicio}
                        onChange={(e) => setNewFechaInicio(e.target.value)}
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">Hasta</label>
                      <input
                        type="date"
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                        value={newFechaFin}
                        onChange={(e) => setNewFechaFin(e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={mode === 'pick' ? handleAddToExisting : handleCreateAndAdd}
            disabled={
              saving || (mode === 'pick' && !selectedColeccionId) || (mode === 'create' && !newNombre.trim())
            }
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Guardando…' : mode === 'pick' ? 'Agregar a colección' : 'Crear y agregar'}
          </button>
        </div>
      </div>
    </div>
  );
};
